/**
 * What the island knows about the page it is on: a short label, an icon name, a one-line hint,
 * and WHERE to sit so it never covers that page's own controls.
 *
 * Placement is the "intelligent" part. Boards and long tables keep their top rows busy (column
 * headers, toolbars), so the island floats at the bottom there; reading pages have their action
 * bars at the top and free space below the header, so it docks top-center under the header.
 * Pure and table-driven so a new page is one row, and so it can be tested.
 */

export type IslandPlacement = 'top' | 'bottom'
export type IslandIcon = 'work' | 'support' | 'reports' | 'approvals' | 'meetings' | 'overdue' | 'clients' | 'projects' | 'people' | 'settings' | 'home'

export interface PageContext {
  key: string
  label: string
  icon: IslandIcon
  placement: IslandPlacement
  /** A tiny page-specific nudge shown in the ticker, when the island has nothing urgent. */
  hint: string
}

interface Rule extends PageContext {
  match: RegExp
}

const RULES: Rule[] = [
  { key: 'work', match: /\/w\/[^/]+\/work/, label: 'Work', icon: 'work', placement: 'bottom', hint: 'Drag a card to move it. ⌘K finds anything.' },
  { key: 'overdue', match: /\/w\/[^/]+\/overdue/, label: 'Overdue', icon: 'overdue', placement: 'bottom', hint: 'Oldest first: clear the longest-waiting item.' },
  { key: 'support-queue', match: /\/w\/[^/]+\/support-ticket(?!s)/, label: 'L3 Support', icon: 'support', placement: 'bottom', hint: 'Your filters are saved and follow you.' },
  { key: 'support', match: /\/w\/[^/]+\/support-tickets/, label: 'Support', icon: 'support', placement: 'bottom', hint: 'Breached tickets sort to the top.' },
  { key: 'reports', match: /\/w\/[^/]+\/reports/, label: 'Reports', icon: 'reports', placement: 'bottom', hint: 'Exports follow your filters and timezone.' },
  { key: 'approvals', match: /\/w\/[^/]+\/approvals/, label: 'Approvals', icon: 'approvals', placement: 'top', hint: 'Approve from Slack, too.' },
  { key: 'meetings', match: /\/w\/[^/]+\/meetings/, label: 'Meetings', icon: 'meetings', placement: 'top', hint: 'Turn an action item into a task in one click.' },
  { key: 'clients', match: /\/w\/[^/]+\/clients/, label: 'Clients', icon: 'clients', placement: 'top', hint: 'Every request ties back to a client.' },
  { key: 'projects', match: /\/w\/[^/]+\/projects/, label: 'Projects', icon: 'projects', placement: 'bottom', hint: 'Plans, milestones and risks live on each project.' },
  { key: 'people', match: /\/w\/[^/]+\/(people|teams)/, label: 'People', icon: 'people', placement: 'top', hint: 'See anyone’s plate from their profile.' },
  { key: 'settings', match: /\/w\/[^/]+\/settings/, label: 'Settings', icon: 'settings', placement: 'bottom', hint: 'Changes apply on the next request.' },
]

const FALLBACK: PageContext = { key: 'home', label: 'TapWork', icon: 'home', placement: 'top', hint: 'Press ⌘K to search everything.' }

export function pageContext(pathname: string): PageContext {
  const rule = RULES.find((r) => r.match.test(pathname))
  if (!rule) return FALLBACK
  const { match: _match, ...ctx } = rule
  return ctx
}

/** Greeting by local hour: a small touch that makes the pill feel alive at a glance. */
export function greeting(date: Date): string {
  const h = date.getHours()
  if (h < 5) return 'Working late'
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  return 'Good evening'
}
